'use client';
import { URL_INHERIT_PARAMS } from '@/constants/urlInheritParams';
import { getCookie, setCookie } from '@/utils/cookies';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { setFlexibleFormSubmitError } from './flexibleFormShared';
import { EMAIL_HANDOFF_COOKIE, MAX_AGE_EMAIL_HANDOFF_COOKIE } from './emailHandoffConfig';
import { isValidEmailHandoff } from './emailHandoffValidation';

interface EmailHandoffSubmitArgs {
    emailFieldName?: string;
    redirectUrl?: string;
}

const cookieOptions = { domain: '.siriusxm.com', path: '/', secure: true, sameSite: 'Lax' } as const;
const errorMessage = 'We could not continue. Please try again.';

export function isUsEmailHandoff(sxmGeo?: string): boolean {
    try {
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname.toLowerCase();
            if (hostname === 'siriusxm.ca' || hostname.endsWith('.siriusxm.ca')) {
                return false;
            }
        }

        return /:US$/i.test(sxmGeo ?? getCookie('sxm_geo') ?? '');
    } catch {
        return false;
    }
}

export function createEmailHandoffCustomSubmit({
    emailFieldName,
    redirectUrl,
}: EmailHandoffSubmitArgs): FlexibleFormVariant['customSubmit'] {
    return async (data, context) => {
        let cookieWritten = false;
        try {
            const email = emailFieldName ? data.get(emailFieldName) : undefined;
            if (typeof email !== 'string' || !isValidEmailHandoff(email) || !isUsEmailHandoff() || !redirectUrl) {
                setFlexibleFormSubmitError(context, errorMessage);
                return;
            }

            const hostname = window.location.hostname.toLowerCase();
            if (
                window.location.protocol !== 'https:' ||
                !(hostname === 'siriusxm.com' || hostname.endsWith('.siriusxm.com'))
            ) {
                setFlexibleFormSubmitError(context, errorMessage);
                return;
            }

            const target = new URL(redirectUrl);
            if (
                target.protocol !== 'https:' ||
                !target.hostname.endsWith('.siriusxm.com') ||
                target.username ||
                target.password
            ) {
                setFlexibleFormSubmitError(context, errorMessage);
                return;
            }

            // Keep the approved inheritance rules even if a caller supplies extra destination parameters.
            const approvedParams = URL_INHERIT_PARAMS.map((param) => param.toLowerCase());
            Array.from(target.searchParams.keys()).forEach((key) => {
                if (key.toLowerCase().includes('email') || !approvedParams.includes(key.toLowerCase())) {
                    target.searchParams.delete(key);
                }
            });
            target.hash = '';

            setCookie(EMAIL_HANDOFF_COOKIE, email, { ...cookieOptions, maxAge: MAX_AGE_EMAIL_HANDOFF_COOKIE });
            cookieWritten = true;
            if (getCookie(EMAIL_HANDOFF_COOKIE) !== email) {
                setFlexibleFormSubmitError(context, errorMessage);
                return;
            }

            window.location.assign(target.toString());
        } catch {
            // Never log the exception: browser/cookie errors may contain the entered address.
            if (cookieWritten) {
                try {
                    setCookie(EMAIL_HANDOFF_COOKIE, '', { ...cookieOptions, maxAge: 0 });
                } catch {
                    // Cookie access may itself be blocked; the original cookie expires after 60 seconds.
                }
            }
            setFlexibleFormSubmitError(context, errorMessage);
        }
    };
}
